import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { MenuItem } from "./MenuItem";

export interface MenuOptionProps {
  icon?: ReactNode;
  children: ReactNode;

  description?: ReactNode;

  /** Overrides the automatic check icon shown when `selected` is true. */
  trailing?: ReactNode;

  selected?: boolean;
  highlighted?: boolean;
  danger?: boolean;
  disabled?: boolean;

  size?: "sm" | "md";

  /** Omit for a non-interactive row (renders a plain span instead of a button). */
  onSelect?: () => void;
  onHighlight?: () => void;

  id?: string;

  /** For a listbox/combobox panel that needs to scroll a highlighted option into view. */
  optionRef?: (node: HTMLButtonElement | null) => void;

  /** "menuitem" for an action list (a context menu, a static picker); "option" for a listbox/combobox. */
  role?: "menuitem" | "option";

  /** False for a row that shares its line with other content (e.g. a trailing pin button) instead of taking the full panel width. */
  fullWidth?: boolean;

  /** "dialog" for an action that opens a dialog (e.g. "Send feedback"). */
  "aria-haspopup"?: "dialog";
}

export function MenuOption({
  icon,
  children,
  description,
  trailing,
  selected = false,
  highlighted = false,
  danger = false,
  disabled = false,
  size = "md",
  onSelect,
  onHighlight,
  id,
  optionRef,
  role = "menuitem",
  fullWidth = true,
  "aria-haspopup": ariaHaspopup,
}: Readonly<MenuOptionProps>) {
  const row = (
    <MenuItem
      size={size}
      leading={icon}
      description={description}
      highlighted={highlighted}
      selected={selected}
      danger={danger}
      disabled={disabled}
      trailing={
        trailing ?? (selected && <Check size={size === "sm" ? 12 : 18} className="shrink-0 text-brand-primary" />)
      }
    >
      {children}
    </MenuItem>
  );

  const className = [
    "group block cursor-pointer border-0 bg-transparent p-0 text-left focus:outline-none disabled:cursor-not-allowed",
    fullWidth ? "w-full" : "min-w-0 flex-1",
  ].join(" ");

  if (!onSelect) {
    return <span className={className}>{row}</span>;
  }

  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: role is dynamic (a prop); aria-selected is only ever set when the caller passes role="option".
    <button
      type="button"
      id={id}
      ref={optionRef}
      role={role}
      tabIndex={id !== undefined ? -1 : undefined}
      disabled={disabled}
      aria-selected={role === "option" ? selected : undefined}
      aria-haspopup={ariaHaspopup}
      onMouseEnter={onHighlight}
      onClick={onSelect}
      className={className}
    >
      {row}
    </button>
  );
}
